import { ChangeDetectionStrategy, Component, computed, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { AdminSidebarComponent } from '../admin-sidebar/admin-sidebar.component';
import { HeaderComponent } from '../header/header.component';
import { BottomNavComponent } from '../bottom-nav/bottom-nav.component';
import { ToastHostComponent } from '../toast-host/toast-host.component';
import { ModalComponent, CatIconComponent } from '@shared/component';
import { PersonaService, ManagedVendorService } from '@shared/service';
import { copyrightLine } from '@shared/utils/legal.content';

/**
 * App shell. Persona-aware chrome mirroring the mockup's three modes:
 *  - vendor:   fixed sidebar + topbar + content + mobile bottom-nav
 *  - admin:    `.amode` — admin-side console layout (no topbar/bottom-nav)
 *  - customer: `.cmode` — no sidebar/topbar, centered content + mobile bottom-nav
 *  - none (login): bare, full-width.
 */
@Component({
  selector: 'app-layout',
  standalone: true,
  styleUrl: './layout.component.scss',
  imports: [
    CommonModule,
    SidebarComponent,
    AdminSidebarComponent,
    HeaderComponent,
    BottomNavComponent,
    ToastHostComponent,
    ModalComponent,
    CatIconComponent,
    RouterLink,
  ],
  templateUrl: './layout.component.html',
  
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent {
  /** "© <year> Inspiration Valley Enterprise. All rights reserved." — rendered on every page. */
  readonly copyrightLine = copyrightLine();

  readonly drawerOpen = signal(false);
  readonly switchOpen = signal(false);
  readonly personaSvc = inject(PersonaService);
  readonly mv = inject(ManagedVendorService);
  private readonly router = inject(Router);

  /**
   * Full-bleed routes manage their own height/scroll flush under the header — the
   * shell content padding is stripped for them (mockup's calendar / detail screens).
   * Grown per-screen in later phases.
   */
  private static readonly FULL_BLEED: string[] = [];
  readonly isFullBleed = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.computeFullBleed()),
      startWith(this.computeFullBleed()),
    ),
    { initialValue: this.computeFullBleed() },
  );

  private computeFullBleed(): boolean {
    const url = this.router.url.split('?')[0];
    return LayoutComponent.FULL_BLEED.some(seg => url.includes('/' + seg));
  }

  /** Current URL as a signal, so chrome recomputes on navigation (guest has no persona). */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /**
   * Routes that must ALWAYS render bare (no sidebar/topbar) regardless of who is logged in —
   * standalone, login-free screens (auth cards, the WhatsApp document re-upload link, the public
   * /legal Terms page, which carries its own brand header). Without
   * this, an admin who opens /reupload in their own session would see it wrapped in admin chrome.
   */
  private static readonly BARE_ROUTES = ['/auth', '/reupload', '/legal'];

  /**
   * Which chrome to render. Bare routes win outright; then a logged-in user's real persona; a guest
   * (null persona) browsing the public /customer area still gets customer chrome (.cmode + customer
   * bottom-nav) rather than the bare layout. Guests never get vendor/admin chrome.
   */
  readonly displayMode = computed<'vendor' | 'admin' | 'customer' | null>(() => {
    const url = this.currentUrl().split('?')[0];
    if (LayoutComponent.BARE_ROUTES.some(r => url === r || url.startsWith(r + '/'))) return null;
    const persona = this.personaSvc.currentPersona();
    if (persona) return persona;
    return url.startsWith('/customer') ? 'customer' : null;
  });

  toggleDrawer() { this.drawerOpen.set(!this.drawerOpen()); }
  closeDrawer()  { this.drawerOpen.set(false); }

  onSwitchVendor() {
    void this.mv.loadOptions();
    this.switchOpen.set(true);
  }

  chooseVendor(vendorId: number) {
    this.switchOpen.set(false);
    this.mv.switchTo(vendorId);
  }
}
